import { inject, ref } from "vue";
import { useGoogleDataLayer } from "./useGoogleDataLayer";
import { updateCartItem } from "../api/cart";

export function useCartItem() {
    const emitter = inject<any>("mitt");
    const { pushUpdateCartItemToDataLayer } = useGoogleDataLayer();
    const loading = ref(false);

    function decreaseCartItemQuantity(
        cartItem: any,
        quantityChange: number,
        shippingTimeId?: number,
    ) {
        updateCartItemQuantity(
            cartItem,
            cartItem.quantity - quantityChange,
            shippingTimeId,
        );
    }

    function increaseCartItemQuantity(
        cartItem: any,
        quantityChange: number,
        shippingTimeId?: number,
    ) {
        updateCartItemQuantity(
            cartItem,
            cartItem.quantity + quantityChange,
            shippingTimeId,
        );
    }

    function deleteCartItem(cartItem: any, shippingTimeId?: number) {
        updateCartItemQuantity(cartItem, 0, shippingTimeId);
    }

    function updateCartItemQuantity(
        cartItem: any,
        quantity: number,
        shippingTimeId?: number,
    ) {
        if (loading.value) {
            return;
        }

        const params: Record<string, any> = {
            quantity: quantity,
        };

        if (cartItem.subscription_option_id) {
            params.subscription_option_id = cartItem.subscription_option_id;
        }

        if (shippingTimeId) {
            params.shipping_time_id = shippingTimeId;
        }

        requestCartItemUpdate(cartItem, params);
    }

    function nextSubscriptionOption(cartItem: any, shippingTimeId?: number) {
        if (nextSubscriptionOptionExists(cartItem)) {
            updateSubscriptionOption(
                cartItem,
                cartItem.subscription_option_id + 1,
                shippingTimeId,
            );
        }
    }

    function nextSubscriptionOptionExists(cartItem: any) {
        for (const id in cartItem.product_option.subscription_options) {
            if (
                cartItem.product_option.subscription_options[id].id ===
                cartItem.subscription_option_id + 1
            ) {
                return true;
            }
        }

        return false;
    }

    function previousSubscriptionOption(
        cartItem: any,
        shippingTimeId?: number,
    ) {
        updateSubscriptionOption(
            cartItem,
            cartItem.subscription_option_id - 1,
            shippingTimeId,
        );
    }

    function updateSubscriptionOption(
        cartItem: any,
        subscriptionOptionId: number,
        shippingTimeId?: number,
    ) {
        if (loading.value) {
            return;
        }

        const params: Record<string, any> = {
            quantity: cartItem.quantity,
        };

        if (subscriptionOptionId) {
            params.subscription_option_id = subscriptionOptionId;
        }

        if (shippingTimeId) {
            params.shipping_time_id = shippingTimeId;
        }

        requestCartItemUpdate(cartItem, params);
    }

    function requestCartItemUpdate(cartItem: any, params: Record<string, any>) {
        if (loading.value) {
            return;
        }

        loading.value = true;

        updateCartItem(cartItem.id, params)
            .then((data) => {
                emitter.emit("cartItemUpdated", {
                    cartItemId: cartItem.id,
                    ...data,
                });

                pushUpdateCartItemToDataLayer({
                    product: cartItem.product_option.product,
                    productOption: cartItem.product_option,
                    subscribe: Object.prototype.hasOwnProperty.call(
                        params,
                        "subscription_option_id",
                    ),
                    quantity: params.quantity - cartItem.quantity,
                    source: "Cart",
                });
            })
            .catch((error) => {
                if (
                    typeof error.response !== "undefined" &&
                    Object.prototype.hasOwnProperty.call(
                        error.response,
                        "data",
                    ) &&
                    Object.prototype.hasOwnProperty.call(
                        error.response.data,
                        "errors",
                    )
                ) {
                    emitter.emit("cartItemError", {
                        cartItemId: cartItem.id,
                        data: error.response.data,
                    });
                }
            })
            .finally(() => (loading.value = false));
    }

    return {
        loading,
        decreaseCartItemQuantity,
        deleteCartItem,
        increaseCartItemQuantity,
        updateCartItemQuantity,
        nextSubscriptionOption,
        nextSubscriptionOptionExists,
        previousSubscriptionOption,
        updateSubscriptionOption,
    };
}
